import * as Sentry from '@sentry/react'

import { env } from '@/env'

import {
  clearGoogleAnalyticsCookies,
  isAnalyticsOptedOut,
  storeAnalyticsOptOut,
} from './analytics.consent'
import {
  CONSENT_ANALYTICS_GRANTED,
  CONSENT_DENIED,
  GTM_SCRIPT_URL,
} from './analytics.constants'
import { parseAnalyticsEvent } from './analytics.events'
import type { AnalyticsEventName, AnalyticsEventParams } from './analytics.events'

declare global {
  interface Window {
    dataLayer?: unknown[]
  }
}

export interface AnalyticsIdentity {
  tenantId: string
  userRole: string
}

type PushEventArgs<EventName extends AnalyticsEventName> =
  Record<string, never> extends AnalyticsEventParams<EventName>
    ? []
    : [params: AnalyticsEventParams<EventName>]

let initPromise: Promise<boolean> | null = null
let isInitialised = false
let pushedIdentityKey: string | null = null

function gtag(..._commandArguments: unknown[]): void {
  // eslint-disable-next-line prefer-rest-params
  window.dataLayer?.push(arguments)
}

export async function sha256Hex(value: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(value))
  return Array.from(new Uint8Array(digest), (byte) =>
    byte.toString(16).padStart(2, '0'),
  ).join('')
}

function injectGtmScript(containerId: string): void {
  const script = document.createElement('script')
  script.async = true
  script.src = `${GTM_SCRIPT_URL}?id=${encodeURIComponent(containerId)}`
  document.head.appendChild(script)
  window.dataLayer?.push({ 'gtm.start': Date.now(), event: 'gtm.js' })
}

async function pushIdentity(identity: AnalyticsIdentity): Promise<void> {
  const previousIdentityKey = pushedIdentityKey
  pushedIdentityKey = `${identity.tenantId}:${identity.userRole}`
  try {
    const tenantHash = await sha256Hex(identity.tenantId)
    window.dataLayer?.push({
      tenant_hash: tenantHash,
      user_role: identity.userRole,
      deploy_env: env.VITE_DEPLOY_ENV,
      app_version: env.VITE_APP_VERSION,
    })
  } catch (caughtError) {
    pushedIdentityKey = previousIdentityKey
    throw caughtError
  }
}

async function loadAnalytics(identity: AnalyticsIdentity): Promise<boolean> {
  const containerId = env.VITE_GTM_CONTAINER_ID
  if (!containerId || isAnalyticsOptedOut()) return false

  window.dataLayer = window.dataLayer ?? []
  gtag('consent', 'default', CONSENT_DENIED)
  await pushIdentity(identity)

  if (isAnalyticsOptedOut()) return false

  gtag('consent', 'update', CONSENT_ANALYTICS_GRANTED)
  injectGtmScript(containerId)

  isInitialised = true
  return true
}

export async function initAnalytics(identity: AnalyticsIdentity): Promise<boolean> {
  initPromise ??= loadAnalytics(identity)
    .catch((caughtError: unknown) => {
      Sentry.captureException(caughtError)
      return false
    })
    .then((hasLoaded) => {
      if (!hasLoaded) initPromise = null
      return hasLoaded
    })

  const hasLoaded = await initPromise
  if (hasLoaded && pushedIdentityKey !== `${identity.tenantId}:${identity.userRole}`) {
    await pushIdentity(identity)
  }
  return hasLoaded
}

export function isAnalyticsConfigured(): boolean {
  return Boolean(env.VITE_GTM_CONTAINER_ID)
}

export function isAnalyticsActive(): boolean {
  return isInitialised && !isAnalyticsOptedOut()
}

export function pushEvent<EventName extends AnalyticsEventName>(
  eventName: EventName,
  ...[params]: PushEventArgs<EventName>
): void {
  if (!isAnalyticsActive()) return

  const parsedEvent = parseAnalyticsEvent(eventName, params ?? {})
  if (!parsedEvent.success) {
    Sentry.addBreadcrumb({
      category: 'analytics',
      level: 'warning',
      message: `Dropped invalid analytics event: ${eventName}`,
    })
    return
  }

  window.dataLayer?.push({ event: eventName, ...parsedEvent.data })
}

export function trackPageView(routePattern: string): void {
  pushEvent('page_view', { route_pattern: routePattern })
}

export function setAnalyticsOptOut(isOptedOut: boolean): void {
  storeAnalyticsOptOut(isOptedOut)
  if (!isInitialised) return

  if (isOptedOut) {
    gtag('consent', 'update', CONSENT_DENIED)
    clearGoogleAnalyticsCookies()
  } else {
    gtag('consent', 'update', CONSENT_ANALYTICS_GRANTED)
  }
}
